import { useCallback } from 'react';

import { buildViewLabel } from 'component/Offer/Service/__hooks/useViewLabel';
import getAccommodationTypeLabel from 'component/Search/Filter/AccommodationType/helper/getAccommodationTypeLabel';
import { getGuestRatingLabel } from 'component/Search/Filter/GuestRating/__helper/guestRatingLabel';
import { getHotelCategoryFilterLabel } from 'component/Search/Filter/HotelCategory/__helper/hotelCategoryFilterLabel';
import { buildRoomTypeLabel } from 'component/Search/Filter/RoomType/__helper/roomTypeLabel';
import { attributeListMap } from 'component/Search/Filter/ScopedAttribute/__config/mapping';

import { getRatingConfig } from 'hooks/useRatingConfig';
import { TranslationFunction, useTranslation } from 'hooks/useTranslation';

import { filterAtom } from 'data/Atom/Filter';
import { searchStore } from 'data/Atom/Store';

import { buildMealTypeFilterLabel } from 'utility/Format/mealType';

import { FilterResultEntry } from 'types/legacy/wireframe';
import {
    AccommodationFilter,
    CancellationType,
    FlightFilter,
    SpecialFilter,
    TransferFilter,
} from 'types/Search/filter';

import {
    AccommodationMeta,
    FilterInformation,
    FlightMeta,
    SpecialMeta,
    TransferMeta,
} from 'proto/Search/FilterMeta';

function addFlightFilterResults(
    flight: FlightFilter,
    filterResults: FilterResultEntry[],
    t: TranslationFunction,
    filterInformation?: FlightMeta
): FilterResultEntry[] {
    if (flight.directFlight) {
        filterResults = [
            ...filterResults,
            buildFilterResultEntry(t('TRANS-direct-flight'), {
                totalCount:
                    filterInformation?.directFlight?.totalCount ?? undefined,
                count: filterInformation?.directFlight?.count ?? undefined,
            }),
        ];
    }

    if (flight.withNoBag) {
        filterResults = [
            ...filterResults,
            buildFilterResultEntry(
                t('TRANS-also-flights-with-hand-baggage-only'),
                {
                    totalCount:
                        filterInformation?.withNoBag?.totalCount ?? undefined,
                    count: filterInformation?.withNoBag?.count ?? undefined,
                }
            ),
        ];
    }

    return filterResults;
}

function addTransferFilterResults(
    transfer: TransferFilter,
    filterResults: FilterResultEntry[],
    t: TranslationFunction,
    filterInformation?: TransferMeta
): FilterResultEntry[] {
    if (transfer.hotelTransfer) {
        filterResults = [
            ...filterResults,
            buildFilterResultEntry(t('TRANS-hotel-transfer'), {
                totalCount:
                    filterInformation?.hotelTransfer?.totalCount ?? undefined,
                count: filterInformation?.hotelTransfer?.count ?? undefined,
            }),
        ];
    }

    if (transfer.privateTransfer) {
        filterResults = [
            ...filterResults,
            buildFilterResultEntry(t('TRANS-private-transfer'), {
                totalCount:
                    filterInformation?.privateTransfer?.totalCount ?? undefined,
                count: filterInformation?.privateTransfer?.count ?? undefined,
            }),
        ];
    }

    if (transfer.railAndFly) {
        filterResults = [
            ...filterResults,
            buildFilterResultEntry(t('TRANS-rail-and-fly'), {
                totalCount:
                    filterInformation?.railAndFly?.totalCount ?? undefined,
                count: filterInformation?.railAndFly?.count ?? undefined,
            }),
        ];
    }

    return filterResults;
}

function addAccommodationFilterResults(
    accommodation: AccommodationFilter,
    filterResults: FilterResultEntry[],
    t: TranslationFunction,
    filterInformation?: AccommodationMeta
): FilterResultEntry[] {
    if (accommodation.mealTypeList.size > 0 && filterInformation?.mealType) {
        const mealTypeList = filterInformation?.mealType;
        filterResults = filterResults.concat(
            [...accommodation.mealTypeList].map((mealType) => {
                const mealTypeData = mealTypeList[mealType] || {};
                const label = buildMealTypeFilterLabel(
                    mealType,
                    t,
                    mealTypeList
                );
                return buildFilterResultEntry(label, {
                    totalCount: mealTypeData.totalCount ?? undefined,
                    count: mealTypeData.count ?? undefined,
                });
            })
        );
    }

    if (accommodation.roomTypeList.size > 0 && filterInformation?.roomType) {
        const roomTypeList = filterInformation?.roomType;
        filterResults = filterResults.concat(
            [...accommodation.roomTypeList].map((roomType) => {
                const roomTypeData = roomTypeList[roomType] || {};
                const label = buildRoomTypeLabel(roomType, t);
                return buildFilterResultEntry(label, {
                    totalCount: roomTypeData.totalCount ?? undefined,
                    count: roomTypeData.count ?? undefined,
                });
            })
        );
    }

    if (accommodation.viewTypeList.size > 0 && filterInformation?.viewType) {
        const viewTypeList = filterInformation?.viewType;
        filterResults = filterResults.concat(
            [...accommodation.viewTypeList].map((viewType) => {
                const viewTypeData = viewTypeList[viewType] || {};
                const label = buildViewLabel(viewType, t);
                return buildFilterResultEntry(label, {
                    totalCount: viewTypeData.totalCount ?? undefined,
                    count: viewTypeData.count ?? undefined,
                });
            })
        );
    }

    if (
        accommodation.hotelCategoryList.size > 0 &&
        filterInformation?.hotelCategory
    ) {
        const hotelCategoryList = filterInformation?.hotelCategory;
        filterResults = filterResults.concat(
            [...accommodation.hotelCategoryList].map((hotelCategory) => {
                const hotelCategoryData =
                    hotelCategoryList[hotelCategory] || {};
                const label = getHotelCategoryFilterLabel(hotelCategory, t);
                return buildFilterResultEntry(label, {
                    totalCount: hotelCategoryData?.totalCount ?? undefined,
                    count: hotelCategoryData?.count ?? undefined,
                });
            })
        );
    }

    if (
        accommodation.accommodationCategoryList.size > 0 &&
        filterInformation?.accommodationCategory
    ) {
        const accommodationCategoryList =
            filterInformation?.accommodationCategory;
        filterResults = filterResults.concat(
            [...accommodation.accommodationCategoryList].map(
                (accommodationCategory) => {
                    const accommodationCategoryData =
                        accommodationCategoryList[accommodationCategory] || {};
                    const label = getAccommodationTypeLabel(
                        accommodationCategory,
                        t
                    );
                    return buildFilterResultEntry(label, {
                        totalCount:
                            accommodationCategoryData?.totalCount ?? undefined,
                        count: accommodationCategoryData?.count ?? undefined,
                    });
                }
            )
        );
    }

    if (
        accommodation.attributesList.size > 0 &&
        filterInformation?.attributes
    ) {
        const attributesList = filterInformation?.attributes;
        filterResults = filterResults.concat(
            [...accommodation.attributesList].map((attribute) => {
                const attributeData = attributesList[attribute] || {};
                const label = t(attributeListMap[attribute] ?? 'TRANS');
                return buildFilterResultEntry(label, {
                    totalCount: attributeData.totalCount ?? undefined,
                    count: attributeData.count ?? undefined,
                });
            })
        );
    }

    if (accommodation.rating) {
        const ratingConfig = getRatingConfig();
        const ratingFilterRadioList = ratingConfig?.filterThresholds;
        const ratingThreshold = ratingFilterRadioList.find(
            (rating) => rating.threshold === accommodation.rating
        );
        if (ratingThreshold) {
            const label = getGuestRatingLabel(
                ratingThreshold.labelKey,
                ratingThreshold.threshold,
                t
            );
            filterResults = [
                ...filterResults,
                buildFilterResultEntry(label, {
                    totalCount:
                        filterInformation?.rating?.[accommodation.rating]
                            ?.totalCount ?? undefined,
                    count:
                        filterInformation?.rating?.[accommodation.rating]
                            ?.count ?? undefined,
                }),
            ];
        }
    }

    return filterResults;
}

function addSpecialFilterResults(
    special: SpecialFilter,
    filterResults: FilterResultEntry[],
    t: TranslationFunction,
    filterInformation?: SpecialMeta
): FilterResultEntry[] {
    if (special.lowDeposit) {
        filterResults = [
            ...filterResults,
            buildFilterResultEntry(t('TRANS-low-deposit'), {
                totalCount:
                    filterInformation?.lowDeposit?.totalCount ?? undefined,
                count: filterInformation?.lowDeposit?.count ?? undefined,
            }),
        ];
    }

    if (special.reservable) {
        filterResults = [
            ...filterResults,
            buildFilterResultEntry(
                t('TRANS-reserve-few-days-without-deposit'),
                {
                    totalCount:
                        filterInformation?.reservable?.totalCount ?? undefined,
                    count: filterInformation?.reservable?.count ?? undefined,
                }
            ),
        ];
    }

    if (special.promoDiscount) {
        filterResults = [
            ...filterResults,
            buildFilterResultEntry(t('TRANS-promo-discounts'), {
                totalCount:
                    filterInformation?.promoDiscount?.totalCount ?? undefined,
                count: filterInformation?.promoDiscount?.count ?? undefined,
            }),
        ];
    }

    if (special.rebookable) {
        filterResults = [
            ...filterResults,
            buildFilterResultEntry(t('TRANS-rebookable-at-no-cost'), {
                totalCount:
                    filterInformation?.rebookable?.totalCount ?? undefined,
                count: filterInformation?.rebookable?.count ?? undefined,
            }),
        ];
    }

    if (special.cancellation === CancellationType.Cost) {
        filterResults = [
            ...filterResults,
            buildFilterResultEntry(t('TRANS-free-or-cheap-cancellation'), {
                totalCount:
                    filterInformation?.cancellation?.cost?.totalCount ??
                    undefined,
                count:
                    filterInformation?.cancellation?.cost?.count ?? undefined,
            }),
        ];
    }

    return filterResults;
}

function buildFilterResultEntry(
    label: string,
    countObj?: { totalCount?: number; count?: number }
): FilterResultEntry {
    return {
        label,
        hotels_requested: countObj?.totalCount ?? null,
        hotels_matched: countObj?.count ?? null,
    };
}

export const useBuildFilterResults = () => {
    const { t } = useTranslation();

    return useCallback(
        (filterInformation: FilterInformation): FilterResultEntry[] => {
            const filterState = searchStore.get(filterAtom);
            let filterResults: FilterResultEntry[] = [];

            if (filterState.budget?.min || filterState.budget?.max) {
                filterResults = [
                    ...filterResults,
                    buildFilterResultEntry(t('TRANS-travel-price-total')),
                ];
            }

            if (
                filterState.tourOperatorList?.size > 0 &&
                filterInformation.tourOperator?.items
            ) {
                const tourOperatorList = filterInformation.tourOperator.items;
                filterResults = filterResults.concat(
                    [...filterState.tourOperatorList]
                        .filter(
                            (tourOperator) => tourOperator in tourOperatorList
                        )
                        .map((tourOperator) => {
                            const tourOperatorData =
                                tourOperatorList[tourOperator];
                            return buildFilterResultEntry(
                                tourOperatorData.label,
                                {
                                    totalCount:
                                        tourOperatorData.meta?.totalCount ??
                                        undefined,
                                    count:
                                        tourOperatorData.meta?.count ??
                                        undefined,
                                }
                            );
                        })
                );
            }

            filterResults = addFlightFilterResults(
                filterState.flight,
                filterResults,
                t,
                filterInformation.flight
            );
            filterResults = addTransferFilterResults(
                filterState.transfer,
                filterResults,
                t,
                filterInformation.transfer
            );
            filterResults = addAccommodationFilterResults(
                filterState.accommodation,
                filterResults,
                t,
                filterInformation.accommodation
            );
            filterResults = addSpecialFilterResults(
                filterState.special,
                filterResults,
                t,
                filterInformation.special
            );

            if (filterState.kiFilter.length > 0) {
                filterResults = [
                    ...filterResults,
                    buildFilterResultEntry(filterState.kiFilter.join(', ')),
                ];
            }

            return filterResults;
        },
        [t]
    );
};
